import React, {
  Component,
  ErrorInfo as ReactErrorInfo,
  ReactNode,
  createContext,
  useContext,
  useState
} from 'react';

// Types
interface CustomErrorInfo {
  error: Error;
  errorInfo: ReactErrorInfo;
  timestamp: number;
}

interface ErrorState {
  errors: CustomErrorInfo[];
  hasGlobalError: boolean;
  globalError?: Error;
}

interface ErrorContextType {
  errorState: ErrorState;
  logError: (error: Error, errorInfo?: any) => void;
  clearErrors: () => void;
  clearGlobalError: () => void;
}

// Module-level error state
let globalErrorState: ErrorState = {
  errors: [],
  hasGlobalError: false
};

// Module-level subscribers for error state changes
let subscribers: ((state: ErrorState) => void)[] = [];

// Function-based error handling functions
export const logError = (error: Error, errorInfo?: any): void => {
  const newError: CustomErrorInfo = {
    error,
    errorInfo,
    timestamp: Date.now()
  };

  globalErrorState = {
    ...globalErrorState,
    errors: [...globalErrorState.errors, newError]
  };

  // Log to console
  console.error('Error logged:', error, errorInfo);

  // Notify subscribers
  notifySubscribers();
};

export const setGlobalError = (error: Error): void => {
  globalErrorState = {
    ...globalErrorState,
    hasGlobalError: true,
    globalError: error
  };
  notifySubscribers();
};

export const clearErrors = (): void => {
  globalErrorState = {
    ...globalErrorState,
    errors: []
  };
  notifySubscribers();
};

export const clearGlobalError = (): void => {
  globalErrorState = {
    ...globalErrorState,
    hasGlobalError: false,
    globalError: undefined
  };
  notifySubscribers();
};

export const getErrorState = (): ErrorState => {
  return { ...globalErrorState };
};

const notifySubscribers = (): void => {
  subscribers.forEach(callback => callback(globalErrorState));
};

const subscribe = (callback: (state: ErrorState) => void): (() => void) => {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter(sub => sub !== callback);
  };
};

// Context for React components
const ErrorContext = createContext<ErrorContextType | null>(null);

// Hook for using error context
export const useErrorHandler = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorHandler must be used within an ErrorProvider');
  }
  return context;
};

// Function-based Error Provider component
export const ErrorProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [errorState, setErrorState] = useState<ErrorState>(globalErrorState);

  React.useEffect(() => {
    const unsubscribe = subscribe(setErrorState);
    return unsubscribe;
  }, []);

  const contextValue: ErrorContextType = {
    errorState,
    logError,
    clearErrors,
    clearGlobalError
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

// Props for the class-based boundary
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// Minimal class-based Error Boundary (required by React)
class ErrorBoundaryClass extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update global error state using function-based approach
    setGlobalError(error);
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ReactErrorInfo) {
    // Log using function-based error handler
    logError(error, errorInfo);
  }

  // Function-based error reset handler using factory
  private handleTryAgain = createTryAgainHandler(() => {
    // This callback is passed to the factory to handle local state reset
    this.setState({ hasError: false, error: undefined });
  });

  public render() {
    // Use function-based utilities to determine error state
    const hasError = shouldShowError(this.state.hasError);

    if (hasError) {
      // Use function-based utility to get current error
      const error = getCurrentError(this.state.error);
      return (
        this.props.fallback || renderErrorFallback(error, this.handleTryAgain)
      );
    }

    return this.props.children;
  }
}

// Function-based error reset
const resetLocalError = (): void => {
  // This function can be used to reset local error state
  // Additional cleanup logic can be added here
  console.log('Local error state reset');
};

// Function-based error state checker
const shouldShowError = (localError: boolean): boolean => {
  const globalState = getErrorState();
  return localError || globalState.hasGlobalError;
};

// Function-based error getter
const getCurrentError = (localError?: Error): Error | undefined => {
  const globalState = getErrorState();
  return localError || globalState.globalError;
};

// Function-based try again handler factory
const createTryAgainHandler = (resetCallback: () => void): (() => void) => {
  return (): void => {
    resetLocalError();
    clearGlobalError();
    resetCallback();
  };
};

// Function-based error fallback renderer
const renderErrorFallback = (
  error: Error | undefined,
  onTryAgain: () => void
): React.ReactElement => {
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Something went wrong.</h2>
      <details style={{ whiteSpace: 'pre-wrap' }}>
        {error && error.toString()}
      </details>
      <button
        onClick={onTryAgain}
        style={{ marginTop: '10px', padding: '8px 16px' }}
      >
        Try Again
      </button>
    </div>
  );
};

// Function-based wrapper component
export const ErrorBoundary: React.FC<Props> = ({ children, fallback }) => {
  return (
    <ErrorProvider>
      <ErrorBoundaryClass fallback={fallback}>{children}</ErrorBoundaryClass>
    </ErrorProvider>
  );
};

// Export additional function-based utilities
export {
  renderErrorFallback,
  resetLocalError,
  shouldShowError,
  getCurrentError,
  createTryAgainHandler
};

// Default export for backward compatibility
export default ErrorBoundary;
